<template>
  <div :class="[rootCss]" :style="rootStyle">
    <table :class="[`${rootCss}-grid`]" :style="style" :id="id" v-if="loading">
      <tr v-if="title || description">
        <td />
        <td :colspan="setup.strings.length">
          <h5 v-if="title" v-html="title" />
          <h6 v-if="description" v-html="description" />
        </td>
      </tr>
      <tr v-if="notes" :class="[`${rootCss}-small-caps`, `${rootCss}-notes`, `${rootCss}-greyed-out`]">
        <td />
        <td v-for="key in setup.tuning">{{ key }}</td>
      </tr>
      <tr v-for="(fret, index) in frets" :key="index" :class="[`${rootCss}-small-caps`, `${rootCss}-neck`]">
        <td :class="['vc-chord-diagram-position', `${rootCss}-greyed-out`]">
          <div v-if="position">{{ romanize(setup.startAt + index) }}</div>
        </td>
        <td v-for="string in setup.strings" :style="{borderBottomColor: `${color}`}">
          <div>
            <div v-if="string.fret === fret" :class="[`${rootCss}-finger`]" :style="dot" />
            <div v-else :class="[`${rootCss}-finger`]" :style="empty"></div>
          </div>
          <div :style="{background: color}" />
        </td>
      </tr>
      <tr v-if="fingers" :class="[`${rootCss}-fingers`, `${rootCss}-small-caps`, `${rootCss}-greyed-out`]">
        <td />
        <td v-for="string in setup.strings" v-html="string.finger" />
      </tr>
    </table>
    <img v-if="canvas" :src="src" :style="style" @load="ready" />
  </div>
</template>
<script>
import htmlToImage from 'html-to-image'

export default {
  name: 'VcChordDiagram',
  data () {
    return {
      rootCss: 'vc-chord-diagram',
      rootStyle: {
        font: 'normal normal .9em Avenir, Roboto, Helvetica, sans-serif'
      },
      frets: 0,
      id: null,
      loading: true,
      src: ''
    }
  },
  props: {
    /**
     * Render as an image instead as html
     * */
    canvas: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Diagram setup
     * */
    setup: {
      type: Object,
      required: true
    },
    /**
     * Diagram title
     * */
    title: {
      type: String,
      required: false
    },
    /**
     * Diagram description
     * */
    description: {
      type: String,
      required: false
    },
    /**
     * Show the fingers bar
     * */
    fingers: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Width of the diagram in pixels or percentage
     * */
    width: {
      type: String,
      required: false,
      default: '100px'
    },
    /**
     * Height of the diagram in pixels
     * */
    height: {
      type: String,
      required: false,
      default: '160px'
    },
    /**
     * Show the notes bar
     * */
    notes: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Show the position bar
     * */
    position: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Diagram color
     * */
    color: {
      type: String,
      required: false,
      default: '#000'
    },
    /**
     * Diagram background color
     * */
    bgColor: {
      type: String,
      required: false,
      default: '#fff'
    },
    /**
     * Size of the finger dot
     * */
    dotSize: {
      type: Number,
      required: false,
      default: 10
    }
  },
  computed: {
    /**
     * Box styles
     * @computed style
     * @return {Object}
     * */
    style () {
      return {
        width: this.width,
        height: this.height,
        color: this.color,
        background: this.bgColor
      }
    },
    /**
     * Finger dot styles
     * @computed dot
     * @return {Object}
     * */
    dot () {
      return {
        background: this.color,
        width: `${this.dotSize}px`,
        height: `${this.dotSize}px`
      }
    },
    /**
     * Empty position styles
     * @computed empty
     * @return {Object}
     * */
    empty () {
      return {
        background: null,
        width: `${this.dotSize}px`,
        height: `${this.dotSize}px`
      }
    }
  },
  mounted () {
    const positions = []
    this.setup.strings.forEach(string => {
      const fret = string.fret
      if (!positions.includes(fret)) {
        positions.push(fret)
      }
    })
    this.frets = positions.length
    if (this.canvas) {
      this.id = this.guid()
      this.$nextTick(() => {
        const node = this.id
        htmlToImage.toSvgDataURL(document.getElementById(node), {
          style: {
            font: this.rootStyle.font,
            width: this.width,
            height: this.height
          }
        })
          .then(dataUrl => {
            this.src = dataUrl
          })
      })
    }
  },
  methods: {
    /**
     * Ready callback
     * @method ready
     * @return void
     * */
    ready () {
      this.loading = false
    },
    /**
     * Create a UUID/GUID
     * @method guid
     * @return {String}
     * */
    guid () {
      function s4 () {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1)
      }
      return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
    },
    /**
     * Romanize Arabic numbers
     * @method romanize
     * @return {String}
     * */
    romanize (num) {
      if (!+num) {
        return NaN
      }
      const digits = String(+num).split('')
      const key = [
        '', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM',
        '', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC',
        '', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'
      ]
      let roman = ''
      let i = 3
      while (i--) {
        roman = (key[+digits.pop() + (i * 10)] || '') + roman
        return Array(+digits.join('') + 1).join('M') + roman
      }
    }
  }
}
</script>
<style>
.vc-chord-diagram,
.vc-chord-diagram *
{
  margin: 0;
  border: 0;
  padding: 0;
  font-weight: normal;
}

.vc-chord-diagram
{
  display: inline-block;
  vertical-align: top;
  cursor: default;
  /*font: normal .9em Avenir, Roboto, Helvetica, sans-serif;*/
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}

.vc-chord-diagram .vc-chord-diagram-small-caps
{
  font-variant: small-caps;
  font-variant: all-small-caps;
}

.vc-chord-diagram .vc-chord-diagram-grid
{
  border-collapse: collapse;
  table-layout: fixed;
}

.vc-chord-diagram .vc-chord-diagram-grid td
{
  vertical-align: middle;
  display: table-cell;
  text-align: center;
}

.vc-chord-diagram h5
{
  font-size: .9rem;
}

.vc-chord-diagram h6
{
  font-size: .5rem;
}

.vc-chord-diagram .vc-chord-diagram-finger
{
  display: inline-block;
  margin: auto;
  border-radius: 100%;
}

.vc-chord-diagram .vc-chord-diagram-neck td:not(:first-child)
{
  border-bottom-style: solid;
  border-bottom-width: 1px;
}

.vc-chord-diagram .vc-chord-diagram-neck td:not(:first-child)
{
  position: relative;
  z-index: 5;
}

.vc-chord-diagram .vc-chord-diagram-neck td:not(:first-child) > div:last-of-type
{
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 100%;
}

.vc-chord-diagram .vc-chord-diagram-greyed-out
{
  opacity: .75;
}

.vc-chord-diagram td.vc-chord-diagram-position
{
  text-align: right;
}

.vc-chord-diagram table + img
{
  display: none;
}
</style>



// WEBPACK FOOTER //
// index.vue?848f0e62